/**
 * Sector ↔ battle adapter: a `BattleSetup` becomes headless-match options,
 * a `MatchReport` plus the final state becomes a `BattleOutcome`.
 *
 * This is the one place that knows how today's harness is driven. When the
 * Match refactor lands `runBattle(setup) → outcome` (BUILDLIST 51.9), this
 * file swaps its implementation and the sector never notices.
 */

import { ammoLeftOf, shortageOf } from './ammunition';
import { structureTypeForSector } from '../core/body';
import { wrecksOf } from './salvage';
import { goLoudOf } from './doctrine';
import { arrivalFates } from '../core/reserves';
import { chronicleFromMatch } from './chronicle';
import { regionFromArrival, spawnableCentroid } from '../core/deploy';
import { createMatchState } from '../match/setup';
import { vecLength, vecNormalize, vecSub } from '../math';
import type { PlanetState, StructureType } from '../types';
import type { GameState } from '../types';
import { objectiveStanding, type HeadlessMatchOptions, type MatchReport } from '../ai/headlessMatch';
import { checkWinCondition } from '../core/gameState';
import { isHighlightShot } from '../core/shotRecord';
import { PHYS_DT } from '../constants';
import { runBattle } from '../match/runBattle';
import { FLOW_PROFILES, JUMP_PROFILES, STEALTH_PROFILES, type BotTuningOverrides } from '../ai/bot/tuning';
import { scarsOf } from './sector';
import type { FactionId, BattleOutcome, BattleSetup, SectorSystem } from './types';

/** Headless options for a setup. Side i is team i; units keep their sector order within the team. */
export function headlessOptionsFor(setup: BattleSetup): HeadlessMatchOptions {
  const tuningFor = (side: BattleSetup['sides'][number]): BotTuningOverrides => ({
    jumping: { ...JUMP_PROFILES[side.doctrine.jump] },
    flow: { ...FLOW_PROFILES[side.doctrine.flow] },
    // the attacker's stance; guards assault. The go-loud clock is the doctrine's, not the profile's (default 0 = sneak to the end).
    ...(setup.opening && side.faction === setup.opening.attacker
      ? { stealth: { ...STEALTH_PROFILES[setup.opening.attackerStance], ...(setup.opening.attackerStance === 'sneak' ? { goLoudRoundsLeft: goLoudOf(side.doctrine) } : {}) } }   // 56.7: the sneak slider's clock
      : {}),
  });
  const defenderIndex = setup.opening ? setup.sides.findIndex(s => s.faction === setup.opening.defender) : -1;
  // R11 step 2: an entered battle's Match logs the switch that opened it as its first input (111)
  const last = setup.fidelity?.[setup.fidelity.length - 1];
  return {
    ...(last?.tier === 'full' ? { firstInputs: [{ kind: 'fidelity' as const, tier: 'full' as const, reason: last.reason }] } : {}),
    seed: setup.seed,
    teams: setup.sides.length,
    unitsByTeam: setup.sides.map(s => s.units.length),
    planets: setup.map.planetCount,
    planetSize: setup.map.planetSize,
    mapLayout: setup.map.layout,
    hazardDensity: setup.map.hazardDensity,
    mode: setup.mode,
    maxRounds: setup.maxRounds,
    difficulty: setup.sides.map(s => s.doctrine.skill),
    personality: setup.sides.map(s => s.doctrine.personality),
    botTuningByTeam: setup.sides.map(tuningFor),
    ...(setup.botParams ? { botParams: setup.botParams } : {}),
    // Every unit's own kit and every side's own supply (RFC Q5: "what you brought", per unit)
    kitByUnit: setup.sides.map(s => s.units.map(u => u.kit)),
    cbp: Math.round(setup.sides.reduce((a, s) => a + s.cbpPerUnit, 0) / setup.sides.length),
    cbpByTeam: setup.sides.map(s => s.cbpPerUnit),
    // 62.3: the drones each side brought, spread over its units
    ...(setup.sides.some(s => (s.drones ?? 0) > 0) ? { dronesByTeam: setup.sides.map(s => s.drones ?? 0) } : {}),
    // Where each side may land: the lane it arrived by (the holder deploys freely) — core/deploy.ts
    ...(deployRegions(setup) ? { deployRegionByTeam: deployRegions(setup)! } : {}),

    knockbackMultiplier: setup.lethality?.knockbackMultiplier ?? 1,
    fallDamageMultiplier: setup.lethality?.fallDamageMultiplier ?? 1,
    // an infiltration: the alert layer on, defenders as guards (Phase 57); the cap is the match's turn limit, so the
    // sneaking policy's clock (goLoudRoundsLeft) has something to read and the mode's own limit rule applies
    ...(setup.opening?.infiltration && defenderIndex >= 0
      ? { startingAlert: setup.opening.startingAlert ?? 'unaware', guardTeams: [defenderIndex], turnLimit: setup.maxRounds }
      : {}),
    mapSeed: setup.map.seed,
    planetsOverride: setup.planets,
    ...(setup.destroyedProps ? { destroyedProps: setup.destroyedProps } : {}),
    // X2: the sides' marks cross down — the other side's landing (its region's facing) is the thing to hit
    ...(setup.marks?.length ? { marks: setup.marks.map(m => { const t = setup.sides.findIndex(x => x.faction === m.faction); const other = setup.sides.find(x => x.faction !== m.faction); return { team: t, fire: m.fire, ...(other ? { regionOf: setup.sides.indexOf(other) } : {}) }; }) } : {}),
    // X6: the weather window the battle is fought in holds throughout
    ...(setup.weather ? { weatherFixed: setup.weather } : {}),
    // X22: exits and withdraw orders (the sector rule)
    ...(setup.extraction ? { extraction: true } : {}),
    ...(setup.callIns ? { callIns: true } : {}),   // X11: units ask for the guns
    // X20: the rounds each side brought (an unlimited side stays unlimited)
    ...(setup.sides.some(s => s.ammo !== undefined) ? { ammoByTeam: setup.sides.map(s => s.ammo) } : {}),
    // X19: the reserves are the respawn pool and arrive at the arrival lane (the respawn layer on, in waves)
    ...(setup.reservesArrive ? { reserves: setup.sides.map(s => s.reserveUnits?.length ?? 0), modeOverrides: { respawn: true } } : {}),
    // units arrive with the health they left the last battle with (data, so the setup can cross to another tab)
    healthByTeam: setup.sides.map(s => s.units.map(u => Math.max(1, Math.min(100, u.health)))),
    // what the holder built, pre-placed (56.5)
    ...(setup.structures.length ? { structuresOverride: setup.structures.map(b => ({ type: battleStructureType(b.kind), planetIndex: b.planetIndex, surfaceDir: b.surfaceDir, ownerTeam: ownerTeamOf(setup, b.owner) })) } : {}),
  };
}

function ownerTeamOf(setup: BattleSetup, faction: string): number | null {
  const i = setup.sides.findIndex(s => s.faction === faction);
  return i >= 0 ? i : null;
}

/** Deploy regions per side: the attacker lands facing the lane it came by; the holder lands where it built. */
function deployRegions(setup: BattleSetup): Array<{ facing: { x: number; y: number; z: number }; halfAngle?: number } | undefined> | null {
  const built = structureRegion(setup);
  const regions = setup.sides.map(s => {
    if (built && s.faction === setup.opening?.defender) return built;
    // X17: a garrison that expects the attack from a lane deploys facing it
    const region = s.arrival ? regionFromArrival(s.arrival.dx, s.arrival.dy) : s.facing ? regionFromArrival(s.facing.dx, s.facing.dy) : undefined;
    // model → full: a side that was in contact lands tight on its lane's side, not spread across a hemisphere
    return region && s.contact ? { ...region, halfAngle: Math.min(region.halfAngle ?? Math.PI / 2, 0.3) } : region;
  });
  return regions.some(r => r) ? regions : null;
}

/** A sector structure in the battle: the structure whose job the sector's word names (X18: BODIES' `sector` column — a depot is the landing pad, a sensor the antenna). */
export function battleStructureType(kind: BattleSetup['structures'][number]['kind']): StructureType {
  return structureTypeForSector(kind);
}

/**
 * The holder deploys where it built: a tight region facing its structures' planet
 * (the arrival region, from fable-1's landing work, stays for the attacker).
 */
export function structureRegion(setup: BattleSetup): { facing: { x: number; y: number; z: number }; halfAngle: number } | null {
  if (!setup.structures.length) return null;
  const planets = setup.planets ?? createMatchState({ ...headlessOptionsForMap(setup), seed: `${setup.seed}-map` }).state.planets;
  const target = planets[setup.structures[0].planetIndex];
  if (!target) return null;
  const d = vecSub(target.position, spawnableCentroid(planets));
  if (vecLength(d) < 1e-6) return null;
  return { facing: vecNormalize(d), halfAngle: 0.35 };
}

/** Only what regenerates the system's map. */
function headlessOptionsForMap(setup: BattleSetup): HeadlessMatchOptions {
  return { seed: setup.seed, teams: 2, unitsByTeam: [1, 1], difficulty: 'easy', planets: setup.map.planetCount, planetSize: setup.map.planetSize, mapLayout: setup.map.layout, hazardDensity: setup.map.hazardDensity, mapSeed: setup.map.seed };
}

/** A system's planets as generated (no scars): the same map every battle there starts from. */
export function planetsForSystem(sys: Pick<SectorSystem, 'mapSeed' | 'planetCount' | 'planetSize' | 'layout' | 'hazardDensity'>): PlanetState[] {
  return createMatchState({ seed: `${sys.mapSeed}-fresh`, teams: 2, unitsByTeam: [1, 1], difficulty: 'easy', planets: sys.planetCount, planetSize: sys.planetSize, mapLayout: sys.layout, hazardDensity: sys.hazardDensity, mapSeed: sys.mapSeed }).state.planets;
}

/**
 * A battle's outcome from its final state alone — what a watched or commanded
 * match hands back (BUILDLIST 56.4): survivors and health from the players,
 * the winner from the win rule, shots and hits from the shot records, loud
 * from the alert layer, the objective lead from the standing. The harness
 * report adds nothing the state does not carry, so auto-resolved, watched and
 * commanded battles all feed the sector through this one function.
 */
export function outcomeFromState(setup: BattleSetup, finalState: GameState, extra: { reason?: string | null; rounds?: number; seconds?: number; gameOver?: boolean } = {}): BattleOutcome {
  const records = finalState.shotRecords ?? [];
  const primary = records.filter(r => !r.submunition);
  const units: BattleOutcome['units'] = [];
  for (let t = 0; t < setup.sides.length; t++) {
    const side = setup.sides[t];
    const teamPlayers = finalState.players.filter(p => p.teamIndex === t).sort((a, b) => a.id - b.id);
    side.units.forEach((u, i) => {
      const p = teamPlayers[i];
      const mine = p ? primary.filter(r => r.shooterId === p.id) : [];
      units.push({
        unitId: u.unitId,
        // X22: an extracted unit is a survivor to the sector
        alive: p ? p.alive || !!p.extracted : false,
        ...(p?.extracted ? { extracted: true } : {}),
        health: p ? p.health : 0,
        kills: p?.kills ?? 0,
        shots: mine.length,
        hits: mine.filter(r => r.damage > 0).length,
      });
    });
  }
  const win = checkWinCondition(finalState);
  const winner = win ? setup.sides[win.winningTeamIndex]?.faction ?? null : null;
  // X20: what the living still carry, and who ran dry
  const ammoLeft = ammoLeftOf(setup, finalState);
  const shortage = shortageOf(setup, finalState);
  // X15: the wrecks this battle leaves — the units that fell, by side and kit (booked after the units are known)
  const wrecksBooked = wrecksOf(setup, { units } as BattleOutcome);
  // X19: what became of the reserves that arrived (the index names the sector's unit)
  const reserveArrivals: BattleOutcome['reserveArrivals'] = {};
  if (finalState.reserveArrivals?.length) {
    for (const a of arrivalFates(finalState)) {
      const side = setup.sides[a.teamIndex];
      const unit = side?.reserveUnits?.[a.index];
      if (!unit) continue;
      (reserveArrivals[side.faction] ??= []).push({ unitId: unit.unitId, alive: a.alive, health: a.alive ? Math.max(1, Math.min(100, a.health)) : 0 });
    }
  }
  // 62.3: drones the living still carry go back up
  const dronesLeft: Partial<Record<FactionId, number>> = {};
  setup.sides.forEach((side, t) => { dronesLeft[side.faction] = finalState.players.filter(p => p.teamIndex === t && (p.alive || p.extracted)).reduce((a, p) => a + (p.drones ?? 0), 0); });
  const alert = finalState.modeState.alert;
  return {
    battleId: setup.id,
    winner,
    reason: extra.reason !== undefined ? extra.reason : win?.reason ?? (extra.gameOver ? 'gameover' : null),
    // R7: an action battle's length is seconds (its turnNumber is rewritten at the time limit and means nothing)
    ...(finalState.config.clock === 'action'
      ? { rounds: 0, clock: 'action' as const, seconds: extra.seconds ?? 0 }
      : { rounds: extra.rounds ?? finalState.turnNumber, clock: 'rounds' as const }),
    units,
    planets: finalState.planets,
    scars: scarsOf(finalState.planets),
    shots: primary.length,
    hits: primary.filter(r => r.damage > 0).length,
    highlightShots: records.filter(r => isHighlightShot(r, PHYS_DT)).length,
    // a straight fight is loud; an infiltration is loud only if the guards reached Alert
    wentLoud: alert ? alert.wentLoud : true,
    objectiveLeader: objectiveLeaderOf(setup, objectiveStanding(finalState)),
    // pre-placed structures are the first N in the match (setup.ts), in order
    structuresSurvived: setup.structures.map((_, i) => (finalState.structures[i]?.health ?? 0) > 0),
    terrainEdits: finalState.terrainEdits ?? [],
    ...(finalState.propEvents?.length ? { propEvents: finalState.propEvents } : {}),
    chronicle: chronicleFromMatch(setup, finalState, extra.seconds, winner, extra.reason !== undefined ? extra.reason : win?.reason ?? null),
    dronesLeft,
    ...(Object.keys(reserveArrivals).length ? { reserveArrivals } : {}),
    ...(ammoLeft ? { ammoLeft } : {}),
    ...(shortage ? { shortage } : {}),
    ...(wrecksBooked ? { wrecks: wrecksBooked } : {}),
    destroyedProps: {
      rocks: finalState.rocks.filter(r => !r.alive).map(r => r.id),
      flora: finalState.flora.filter(f => !f.alive).map(f => f.id),
      hazards: finalState.hazards.filter(h => !h.alive).map(h => h.id),
    },
  };
}

/** Map a finished harness match back onto the setup's units (the report's rounds and reason, the state's everything else). */
export function outcomeFrom(setup: BattleSetup, report: MatchReport, finalState: GameState): BattleOutcome {
  return outcomeFromState(setup, finalState, { reason: report.reason, rounds: report.rounds, seconds: report.seconds });
}

/** The side ahead on the objective standing ("N salvage", "N planets", …), or null when tied or no objective. */
export function objectiveLeaderOf(setup: BattleSetup, objective: Record<number, string> | null): BattleOutcome['objectiveLeader'] {
  if (!objective) return null;
  const scores = setup.sides.map((s, i) => ({ faction: s.faction, value: parseFloat(objective[i] ?? '0') || 0 }));
  scores.sort((a, b) => b.value - a.value);
  return scores.length > 1 && scores[0].value > scores[1].value ? scores[0].faction : null;
}

/**
 * The setup as a file the game can load once it starts from a setup (51.9b):
 * the match options without the live planets (they are not JSON — the crater
 * log, 56.6, is the serialisable form) and without the function hooks.
 */
export function exportMatchSetup(setup: BattleSetup): Omit<HeadlessMatchOptions, 'planetsOverride' | 'prepareState' | 'onFinished'> & { sector: { battleId: string; systemId: string; day: number; sides: Array<{ faction: string; units: string[] }> } } {
  const { planetsOverride: _p, prepareState: _s, onFinished: _f, ...rest } = headlessOptionsFor(setup);
  return { ...rest, sector: { battleId: setup.id, systemId: setup.systemId, day: setup.day, sides: setup.sides.map(s => ({ faction: s.faction, units: s.units.map(u => u.unitId) })) } };
}

/**
 * The battle as a message to the game page (56.4, take command / watch): the
 * exported setup plus the live planets (typed arrays cross a postMessage
 * boundary; functions do not), and which side the human takes, if any.
 */
export function sectorBattleRequest(setup: BattleSetup, humanFaction: string | null): SectorBattleRequest {
  const humanTeam = humanFaction === null ? null : setup.sides.findIndex(s => s.faction === humanFaction);
  return {
    type: 'orbituary:sectorBattle',
    battleId: setup.id,
    setup: { ...exportMatchSetup(setup), planetsOverride: setup.planets },
    humanTeam: humanTeam === -1 ? null : humanTeam,
  };
}

/** The game page says it is listening (sent to its opener on load with `?sectorBattle`). */
export interface SectorBattleReady { type: 'orbituary:sectorBattleReady' }
/** Sheet → game: play this battle. */
export interface SectorBattleRequest {
  type: 'orbituary:sectorBattle';
  battleId: string;
  setup: Omit<HeadlessMatchOptions, 'prepareState' | 'onFinished'> & { sector: { battleId: string } };
  humanTeam: number | null;
}
/** Game → sheet: the battle ended; here is the final state. */
export interface SectorBattleDone { type: 'orbituary:sectorBattleDone'; battleId: string; state: GameState }

export function isSectorBattleReady(m: unknown): m is SectorBattleReady { return !!m && (m as { type?: string }).type === 'orbituary:sectorBattleReady'; }
export function isSectorBattleRequest(m: unknown): m is SectorBattleRequest { return !!m && (m as { type?: string }).type === 'orbituary:sectorBattle'; }
export function isSectorBattleDone(m: unknown): m is SectorBattleDone { return !!m && (m as { type?: string }).type === 'orbituary:sectorBattleDone'; }

/** Resolve a setup on the headless harness. */
export async function resolveBattle(setup: BattleSetup): Promise<{ outcome: BattleOutcome; report: MatchReport; battleVersion: number }> {
  // Match.runBattle (Phase 51.9): the game's own rules and bots, from the setup to the end
  const { report, state, battleVersion } = await runBattle(headlessOptionsFor(setup));
  return { outcome: { ...outcomeFrom(setup, report, state), battleVersion }, report, battleVersion };
}
